import React, { useState } from "react";
import { Calendar, Tooltip, Row, Col, Spin } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./attendanceCalendar.css";
import { useDispatch } from "react-redux";
import {
  fetchCalendar,
  // fetchTodaySummary,
  fetchPendingRequests,
} from "../attendanceOverviewThunk";
import AttendanceDaySummary from "./AttendanceDaySummary";
import { setSelectedDate } from "../attendanceOverviewSlice";

dayjs.extend(utc);
dayjs.extend(timezone);

const AttendanceCalendar = ({ calendarData, loading }) => {
  const dispatch = useDispatch();

  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const toLocalDay = (date) => dayjs.utc(date).local().format("YYYY-MM-DD");

  // ✅ Trigger when user navigates month/year
  const handlePanelChange = (date) => {
    if (!date) return;
    const month = date.format("MM");
    const year = date.format("YYYY");
    setCurrentMonth(date);
    dispatch(fetchCalendar({ month, year }));
    dispatch(fetchPendingRequests({ month, year }));
  };

  // ✅ Trigger when user clicks a date
  // const handleSelect = (date) => {
  //   if (!date) return;
  //   const formattedDate = date.format("YYYY-MM-DD");
  //   dispatch(fetchTodaySummary({formattedDate}));
  // };
  const handleSelect = (date) => {
    if (!date) return;
    setCurrentMonth(date);
    dispatch(setSelectedDate(date.format("YYYY-MM-DD")));
    // dispatch(
    //   fetchEmployeeAttendanceSummary({
    //     date: date.format("YYYY-MM-DD"),
    //   })
    // );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Extract and normalize data arrays
  // const holidays = (calendarData?.holidays || []).map((h) =>
  //   dayjs(h.holiday_date)
  // );
  // const restricted = (calendarData?.restricted_holidays || []).map((h) =>
  //   dayjs(h.restricted_holiday_date)
  // );
  // const pending = (calendarData?.pending || []).map((p) =>
  //   dayjs(p.attendance_date)
  // );
  // const inprogress = (calendarData?.inprogress || []).map((p) =>
  //   dayjs(p.attendance_date)
  // );
  const holidays = (calendarData?.holidays || []).map(h => toLocalDay(h.holiday_date));
  const restricted = (calendarData?.restricted_holidays || []).map(h => toLocalDay(h.restricted_holiday_date));
  const pending = (calendarData?.pending || []).map(p => toLocalDay(p.attendance_date));
  const inprogress = (calendarData?.inprogress || []).map(p => toLocalDay(p.attendance_date));

  const compensatory = (calendarData?.attendance || [])
    .filter((a) => a.attendance === "compensatory_off")
    // .map((a) => dayjs(a.attendance_date));
    .map(a => toLocalDay(a.attendance_date));
  const present = (calendarData?.attendance || [])
    .filter((a) => a.attendance === "present")
    // .map((a) => dayjs(a.attendance_date));
    .map(a => toLocalDay(a.attendance_date));
  const leave = (calendarData?.attendance || [])
    .filter((a) =>
      [
        "sick_leave",
        "casual_leave",
        "leave_with_pay",
        "leave_without_pay",
        "annual_leave",
        "short_leave",
        "short_casual_leave",
        "short_sick_leave",
      ].includes(a.attendance)
    )
    // .map((a) => dayjs(a.attendance_date));
    .map(a => toLocalDay(a.attendance_date));

  const dateFullCellRender = (value) => {
    // Hide dates from previous/next month
    if (!value.isSame(currentMonth, "month")) {
      return (
        <div
          style={{
            visibility: "hidden",
            height: "100%",
          }}
        />
      );
    }
    const dayStr = value.format("YYYY-MM-DD");
    const isToday = dayjs().isSame(value, "day");
    const isPast = value.isBefore(dayjs(), "day");
    const weekendDays =  calendarData?.weekendDays?.[0]?.weekend_days?.split(",")?.map(Number) || [];
    

    // Determine status
    let status = "";
    // if (holidays.some((d) => d.format("YYYY-MM-DD") === dayStr))
    if (holidays.includes(dayStr)) 
      status = "Holiday";
    // else if (restricted.some((d) => d.format("YYYY-MM-DD") === dayStr))
    else if (restricted.includes(dayStr))
      status = "Restricted";
    // else if (present.some((d) => d.format("YYYY-MM-DD") === dayStr))
    else if (present.includes(dayStr))
      status = "Present";
    // else if (pending.some((d) => d.format("YYYY-MM-DD") === dayStr))
    // else if (leave.some((d) => d.format("YYYY-MM-DD") === dayStr))
    else if (leave.includes(dayStr))
      status = "Leave";
    else if (pending.includes(dayStr))
      status = "Pending";
    // else if (inprogress.some((d) => d.format("YYYY-MM-DD") === dayStr))
    else if (inprogress.includes(dayStr))
      status = "inprogress";
    // else if (compensatory.some((d) => d.format("YYYY-MM-DD") === dayStr))
    else if (compensatory.includes(dayStr))
      status = "Compensatoryoff";
    else if (weekendDays.includes(value.day())) status = "Weekend";
    else if (isToday) status = "Today";
    else if (isPast) status = "Absent"; // mark as Absent if before today and not matched above

    return (
      <Tooltip title={status}>
        <div
          className={`attendance-date-cell ${status.toLowerCase()}`}
          // onClick={() => handleSelectDate(value)}
        >
          {value.date()}
        </div>
      </Tooltip>
    );
  };

  return (
    <div className="attendance-calendar-container">
      <Calendar
        fullscreen={false}
        fullCellRender={dateFullCellRender}
        onPanelChange={handlePanelChange} // ✅ Month change event
        onSelect={handleSelect} // ✅ Date click event
        value={currentMonth}
      />

      {/* Legend */}
      <Row
        gutter={[16, 16]}
        style={{ marginTop: 24, justifyContent: "center" }}
      >
        {[
          { label: "Present", className: "present" },
          { label: "Leave", className: "leave" },
          { label: "Pending", className: "pending" },
          { label: "In-Progress", className: "inprogress" },
          { label: "Holiday", className: "holiday" },
          { label: "Restricted Holiday", className: "restricted" },
          { label: "Compensatory off", className: "compensatoryoff" },
          { label: "Absent", className: "absent" },
          { label: "Today", className: "today" },
          { label: "Weekend", className: "weekend" },
        ].map((item) => (
          <Col
            key={item.label}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span className={`legend-box ${item.className}`} />
            <span>{item.label}</span>
          </Col>
        ))}

      </Row>
        <AttendanceDaySummary />
    </div>
  );
};

export default AttendanceCalendar;
